import { injectable } from 'inversify';
import { action, computed, observable } from 'mobx';
import { ExerciseAdapter } from '../../adapters/answers/ExerciseAdapter';
import { ExplanationAdapter } from '../../adapters/answers/ExplanationAdapter';
import { AnswersAdapter } from '../../adapters/answers/AnswersAdapter';
import {
    CardModel,
    CardType,
    EvaluationMark,
    ExerciseModel,
    ExplanationModel
} from '../../interfaces/models/CardModel';
import Logger from '../../utils/Logger';
import { CardStore } from '../CardStore';

export interface AnswersInputModel extends CardModel {
    exercise?: ExerciseModel;
    explanation?: ExplanationModel;
}

@injectable()
export class AnswersStore {

    log: string[] = [];
    adapter: AnswersAdapter;

    @observable isAnswerError = false;
    @observable isFinished = false;
    @observable currentPlaceholderIndex = 0;
    @observable mark = EvaluationMark.EASY;

    @computed get card(): AnswersInputModel {
        return this.cardStore.currentCard as AnswersInputModel;
    }

    constructor(
        protected cardStore: CardStore
    ) {}

    /**
     * Initialize adapter depending on the card type
     */
    initialize() {
        switch (this.card.card_type) {
            case CardType.EXERCISE:
                this.adapter = new ExerciseAdapter(this.card.exercise);
                break;
            case CardType.EXPLANATION:
                this.adapter = new ExplanationAdapter(this.card.explanation);
                break;
        }
    }

    /**
     * Choose and verify the answer
     *
     * @param index - answer index
     */
    @action.bound async answer(index) {
        this.isAnswerError = false;

        const originalAnswers = this.adapter.answers.filter(answer => answer.index === index);

        if (!originalAnswers.length) {
            return null;
        }

        const originalAnswer = originalAnswers[0];
        this.log.push(`[${this.currentPlaceholderIndex}]: ` + originalAnswer.answer);

        if (this.adapter.verifyAnswer(index, this.currentPlaceholderIndex)) {
            const placeholderIndex = this.currentPlaceholderIndex;
            this.currentPlaceholderIndex++;

            if (placeholderIndex === this.adapter.totalPlaceholders - 1) {
                this.isFinished = true;

                return () => this.cardStore.answerGrammarCard(this.log.join('; '), this.mark);
            }

            return () => void 0;
        } else {
            this.isAnswerError = true;
            Logger.info(`wrong answer for index ${this.currentPlaceholderIndex}`);

            return () => void 0;
        }
    }

    reset() {
        this.adapter = null;
        this.isAnswerError = false;
        this.currentPlaceholderIndex = 0;
        this.initialize();
    }

}
